'use client';
/**
 * Data route. Canonical embedded URL: `/data?embedded=true` (and e.g. `?embedded=true&dataId=...` for tree view).
 */
import { useSearchParams } from 'next/navigation';
import { Box } from '@mui/material';
import { useRef } from 'react';
import withResourceAuth from '@/components/auth/withResourceAuth';
import DataTreeArea from '@/components/dataTree/DataTreeArea';
import { DataPackageSearchPanel } from '@/components/tables/search/DataPackageSearchPanel';
import { DataPackageGrid } from '@/components/tables/DataPackageGrid';
import { tableContainerStyle } from '@/styles/tableStyles';
import { EMBEDDED_QUERY_PARAM, isEmbeddedQueryValue } from '@/lib/embeddedQuery';

function DataPage() {
  const searchParams = useSearchParams();
  const dataId = searchParams.get('dataId');
  const isEmbedded = isEmbeddedQueryValue(searchParams.get(EMBEDDED_QUERY_PARAM));
  const gridRef = useRef<{ refresh: () => void } | null>(null);

  const embeddedContainerSx = isEmbedded
    ? {
        flex: 1,
        minHeight: '100vh',
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
      }
    : undefined;

  // If dataId is provided, show the tree view
  if (dataId) {
    if (isEmbedded) {
      return (
        <Box sx={embeddedContainerSx}>
          <DataTreeArea dataId={dataId} />
        </Box>
      );
    }
    return <DataTreeArea dataId={dataId} />;
  }

  // Handle refresh callback from grid
  const handleGridRefresh = (refreshFn: () => void) => {
    gridRef.current = { refresh: refreshFn };
  };

  // Otherwise, show the search and grid view
  return (
    <Box
      sx={{
        ...tableContainerStyle,
        display: 'flex',
        flexDirection: 'column',
        ...(isEmbedded
          ? {
              height: '100vh',
              flex: 1,
              minHeight: '100vh',
              width: '100%',
            }
          : {}),
      }}
    >
      <DataPackageSearchPanel hideTitleRow={isEmbedded} />
      <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>
        <DataPackageGrid onRefresh={handleGridRefresh} />
      </Box>
    </Box>
  );
}

export default withResourceAuth(DataPage, {
  resourceType: 'data',
  route: 'data',
});
